import { DEFEAT_SECONDS, nextDefeatAge } from './render/defeat';
/**
 * Bootstrap + game loop. Fixed-step sim (1/120 s, capped catch-up) inside the engine's render
 * loop; render layers mirror sim state once per frame. Restart rebuilds the sim in place.
 */
import { Matrix, Mesh, SceneInstrumentation, Vector3 } from '@babylonjs/core/pure';
import type { Camera, DefaultRenderingPipeline, FreeCamera, Scene } from '@babylonjs/core/pure';
import { createEngine } from './gfx/engine';
import { REVEAL_BUDGET_MS, waitForReveal, type BootReadout, type RevealDecision, type RevealSnapshot } from './gfx/reveal';
import { detectTier, QualityController } from './gfx/quality';
import { createWorldScene, shadowCasterTriangles } from './render/scene';
import { chaseFraming } from './render/chaseCamera';
import { BossContact } from './render/bossContact';
import type { CharacterShadows, RenderMaterials } from './render/scene';
import { Bridge } from './render/world';
import {
  BLOB_AUTHORED_GROUND,
  BLOB_MARGIN,
  Crate,
  crowdExtent,
  Enemies,
  Gates,
  Footprint,
  Hazards,
  Pickups,
  Obstacles,
  Squad,
  toWorldX,
} from './render/actors';
import { SkeletalBoss } from './render/boss.skeletal';
import { TankBoss, tankMuzzle } from './render/tank';
import { HeliBoss, heliMuzzle, HELI_HOVER_Y, HELI_PARTS } from './render/heli';
import { SkeletalSquad } from './render/squad.skeletal';
import { Debris } from './render/debris';
import { Ambient } from './render/ambient';
import { cameraImpulse, Fx, type BossFx } from './render/fx';
import { ENEMY_LABEL_Y, hpBars, projectHpBars, projectPoint } from './render/hpbars';
import { createHud, type Hud } from './render/hud';
import { createAudio, type AudioDirector } from './audio/audio';
import { bindAudioControls } from './game/audioControls';
import { THEME_L1, THEME_L2, THEME_L3 } from './audio/music';
import { setGaitBaseHz } from './render/actors';
import { GODMODE_BOSS_N_FLOOR, readDebugHooks, type DebugHooks } from './game/debugParams';
import { InputController } from './game/input';
import { createSessionLevelStore, nextLevelHref, nextStoredLevel, readBootLevelIndex, restartsInPlace } from './game/levelStore';
import { chooseExpeditionRoute, createExpedition, createExpeditionStore, extractExpedition, newExpedition, parseExpeditionSave, resolveExpedition, type ExpeditionSave } from './game/expedition';
import { campaignHref, expeditionHref } from './game/expeditionNavigation';
import { createExpeditionUi, mountExpeditionEntry } from './game/expeditionUi';
import { createExpeditionSim, generateExpedition } from './sim/expedition';
import { ENEMY_LOOKAHEAD, SHIPPED_LEVELS, streamsFor, type LevelSpec } from './sim/level';
import { createSim, drainEvents, enemyThreat, stepSim, type SimState } from './sim/sim';

import { allocateLight, createLightStore, deployLight, lightLevel, newLight, parseLight, settleLight, type LightSave } from './game/lastLight';
import { createLightUi, mountLightEntry } from './game/lastLightUi';

const SIM_DT = 1 / 120;
const MAX_STEPS_PER_FRAME = 8;
/** Plan 19 D5: per-level music identity — one entry per shipped level, indexed by levelIndex
 *  (SHIPPED_LEVELS order). The `?? THEME_L1` at the call site is a defensive default, never hit
 *  while the table and SHIPPED_LEVELS stay the same length. */
const LEVEL_THEMES = [THEME_L1, THEME_L2, THEME_L3];

/**
 * Plan 19 D1 (rev 2): the boss actor swaps on `spec.kind` through an EXHAUSTIVE switch with a
 * `never` default — a fourth kind becomes a type error here, not a silent brute fallback
 * (the plan-gate r1 requirement). The remaining kind checks (the caster-name pushes below, the
 * heli bossFire branch in sim.ts) are deliberate per-site data decisions, not dispatch — a
 * fourth kind still trips THIS switch first at boot. All three actors expose the same
 * `update(sim, dt)` surface the render loop calls.
 */
function createBossActor(scene: Scene, materials: RenderMaterials, spec: LevelSpec['boss']): {
  update(sim: SimState, dt: number, presentationAge?: number): void;
} {
  switch (spec.kind) {
    case 'tank':
      return new TankBoss(scene, materials, spec);
    case 'heli':
      return new HeliBoss(scene, materials, spec);
    case 'brute':
    case undefined:
      return new SkeletalBoss(scene, materials, spec);
    default: {
      const exhausted: never = spec.kind;
      throw new Error(`[boot] unknown boss kind ${JSON.stringify(exhausted)}`);
    }
  }
}
/**
 * Plan 07: the probe/game-loop surface both squad paths satisfy. The procedural Squad (thin
 * instances, Low/Medium) and the per-soldier SkeletalSquad (High) publish the same facts — count,
 * bob sample, framing — so buildProbe and tools/inspect.mjs stay tier-agnostic.
 */
interface SquadSurface {
  muzzlePosition(slot: number): { x: number; y: number; z: number } | null;
  update(sim: SimState, maxInstances: number): void;
  disableBlob(): void;
  debugAnim(): { frontBobY: number; backBobY: number; method: string; pure: boolean };
  debugThinInstances(): { name: string; count: number; zMin: number | null; zMax: number | null };
  debugFraming(camera: Camera): Record<string, unknown>;
}

/**
 * Plan 21: the session-scoped level store — progress lives in sessionStorage (death / final win
 * reset the run to 1; a win with a following level stores current+1), so the URL no longer
 * carries level state. Module-level: readLevelIndex and the end-latch write share ONE instance
 * (and one sticky memory fallback if storage throws).
 */
const levelStore = createSessionLevelStore();
const expeditionStore = createExpeditionStore();

/**
 * Boot level. Plan 04 P1.7 made `?level=N` the whole level-select UI; plan 21 demotes it to an
 * explicit OVERRIDE (automation + deep links — integer-only, clamped to what ships) while
 * session storage is the default source, so the address bar never has to carry the level. No
 * menu system (plan non-goal): the end overlay's buttons remain the whole level-flow UI.
 */
function readLevelIndex(): number {
  return readBootLevelIndex(SHIPPED_LEVELS.length, new URLSearchParams(location.search), levelStore);
}

async function main(): Promise<void> {
  const canvas = document.getElementById('game') as HTMLCanvasElement;
  const loading = document.getElementById('loading')!;
  const params = new URLSearchParams(location.search);
  const isLight = params.get('mode') === 'last-light' || (!params.has('mode') && !params.has('level'));
  const lightStore = createLightStore();
  let lightSave: LightSave | null = isLight ? parseLight(params.get('lightState')) ?? lightStore.read() ?? newLight() : null;
  const saveLight = (s: LightSave) => { lightSave = s; lightStore.write(s); const url = new URL(location.href); url.searchParams.set('mode', 'last-light'); if (!lightStore.persistent) url.searchParams.set('lightState', JSON.stringify(s)); else url.searchParams.delete('lightState'); history.replaceState(null, '', url); };
  const travelLight = (s: LightSave) => { saveLight(s); location.reload(); };
  if (lightSave) saveLight(lightSave);
  const isExpedition = params.get('mode') === 'expedition';
  const randomSeed = (): string => crypto.getRandomValues(new Uint32Array(1))[0].toString(36);
  const storedExpedition = expeditionStore.read();
  const savedExpedition = parseExpeditionSave(params.get('expeditionState')) ?? storedExpedition;
  let expeditionSave: ExpeditionSave | null = isExpedition
    ? savedExpedition ?? createExpedition(params.get('seed') || randomSeed())
    : null;
  const saveExpedition = (save: ExpeditionSave): void => {
    expeditionSave = save;
    expeditionStore.write(save);
    // Replace the fallback checkpoint at settlement too: F5 must not replay its reward.
    history.replaceState(null, '', expeditionHref(location.href, save, expeditionStore.persistent));
  };
  const travelExpedition = (save: ExpeditionSave): void => {
    saveExpedition(save);
    location.reload();
  };
  const launchExpedition = (seed: string): void => {
    const previous = expeditionSave ?? savedExpedition;
    const nextSeed = seed.trim() || randomSeed();
    travelExpedition(previous ? newExpedition(previous, nextSeed) : createExpedition(nextSeed));
  };
  if (expeditionSave) saveExpedition(expeditionSave);

  const { engine, backend, backendLabel } = await createEngine(canvas);
  (window as { __jreb1dBackend?: string }).__jreb1dBackend = backendLabel;
  const tier = detectTier(backend);
  const quality = new QualityController(engine, tier);

  // Plan 17 P0: derived BEFORE createWorldScene/Bridge/createAudio so every level-theme consumer
  // (D3 music theme, D4 environment theme, D2 name display) reads ONE binding — a pure move from
  // below the await, zero behavior change.
  const levelIndex = lightSave ? (lightSave.run.depth - 1) % 3 : expeditionSave ? (expeditionSave.run.depth - 1) % 3 : readLevelIndex();
  const expeditionGeneration = expeditionSave
    ? generateExpedition(expeditionSave.run.seed, expeditionSave.run.depth, expeditionSave.run.route)
    : null;
  const level = (lightSave ? lightLevel(lightSave) : null) ?? expeditionGeneration?.level ?? SHIPPED_LEVELS[levelIndex]!;
  const enemyLookahead = expeditionGeneration || lightSave ? 30 : ENEMY_LOOKAHEAD;
  const hasNextLevel = levelIndex + 1 < SHIPPED_LEVELS.length;
  // Plan 17 D4: one binding from the SAME hoisted levelIndex drives both environment-theme consumers.
  // Plan 19 D5 adds the third branch: L3 Operation Duskline runs the dusk regrade.
  const envTheme = levelIndex === 1 ? 'industrial' : levelIndex === 2 ? 'dusk' : 'overcast';

  // Plan 17 D2: fill the start screen's level line at boot (the static `#start .sub` ships EMPTY —
  // a filled L1 fallback would flash the wrong level on ?level=2 during the bundle download).
  // This runs long before #start can show (that happens at the end of main(), after the awaits).
  // Guarded: cosmetic furniture, it must never take the boot down.
  const startLevelLine = document.querySelector<HTMLElement>('#start .sub');
  if (startLevelLine) startLevelLine.textContent = expeditionSave
    ? `BRIDGE ${expeditionSave.run.depth} — ${expeditionSave.run.route.toUpperCase()} · ${['BRUTE', 'TANK', 'HELICOPTER'][levelIndex]}`
    : `LEVEL ${levelIndex + 1} — ${level.name.toUpperCase()}`;

  const { scene, camera, materials, shadows, pipeline } = await createWorldScene(engine, {
    realShadows: quality.caps.realShadows,
    bloom: quality.caps.bloom,
    msaaSamples: quality.caps.msaaSamples,
    theme: envTheme,
  });
  // Plan 20 P20.4: the tier threads through the Bridge into buildDressing AND structures
  // (world.ts :140). undefined would keep lane A's byte-identical no-opts path (golden-pinned
  // in world.test.ts); every real tier builds the themed vocabulary + structures.
  const bridge = new Bridge(scene, materials, quality.caps.bridgeSegments, envTheme, quality.tier);
  // Impl gate F3 (opus P2-1/copilot P2): the actors take the ACTIVE level. `LevelSpec` satisfies the
  // `MotionLevel` structural type, so the scramble ramp reads the level's own boss trigger z (L2 470,
  // not the 480 fallback the default carried) and the recoil interval comes from the level's weapon
  // ladder. Both defaults stay in the constructors for the unit tests that build actors bare.
  //
  // Plan 07 (skeletal prototype): High tier runs the per-soldier skinned squad (walk/fire/death
  // clips on real bones — thin instances cannot carry per-soldier poses); Low/Medium keep the
  // procedural thin crowd (same placement math, same probe surface). The boss is skeletal on
  // EVERY tier (one actor, one skeleton — the merged-mesh pose approximation it replaces cost
  // more than the rig). `?squad=procedural` forces the old squad for A/B comparison shots.
  const forceProceduralSquad = new URLSearchParams(location.search).get('squad') === 'procedural';
  const useSkeletalSquad = quality.caps.realShadows && !forceProceduralSquad;
  const squad: SquadSurface = useSkeletalSquad
    ? new SkeletalSquad(scene, materials, quality.caps.maxSoldierInstances, level)
    : new Squad(scene, materials, quality.caps.maxSoldierInstances, level);
  // P5.1: its own cap (was borrowing maxSoldierInstances) — the gate asserts the enemy mesh against
  // caps.maxEnemyInstances, so the construction site has to read the same field.
  const enemies = new Enemies(scene, materials, quality.caps.maxEnemyInstances, level, level);
  const gates = new Gates(scene, materials, level);
  // L2 ships three crates (the ladder to tier 3), so the prop is a list, not a singleton.
  const crates = level.crates.map((spec) => new Crate(scene, materials, spec));
  const hazards = new Hazards(scene, materials, level); // plan 15: spike strips — L1 ships two, L2 four
  const footprint = new Footprint(scene, materials); // plan 12/13: the squad's hit width on the deck
  const pickups = new Pickups(scene, materials, level); // plan 13: drops from wiped-out blocks
  const obstacles = new Obstacles(scene, materials, level); // plan 09; plan 14 barrel trio + plan 15 chain pair on L1
  const viewProj = Matrix.Identity(); // HP-bar projection scratch, rewritten every frame
  const camTarget = new Vector3(0, 0, 0); // audit #14: camera.setTarget scratch — setTarget consumes the vector synchronously (LookAt math only), so one scratch is behaviour-identical
  const bossContact = new BossContact(scene, level.boss);
  const cameraFraming = chaseFraming(canvas.clientWidth, canvas.clientHeight, level.boss.z);
  // Plan 15 P15.3: the boss swaps by spec kind — L2 is the tank (three rigid nodes, tank.ts),
  // L1 keeps the rigged brute (plan 07). Plan 19 D1 adds the heli (heli.ts, the tank's pose
  // discipline on a hovering hull). No in-session level transition exists (?level= reloads
  // the page), so one actor per page load and no disposal path is needed.
  const boss = createBossActor(scene, materials, level.boss);
  // Plan 19 D3: the boss-phase FX heights ride the kind — the heli's rocket pods fire from
  // hover + podY, its hull centre sits at HELI_HOVER_Y, and the squad tracers rise to the hull
  // (at 2.6 m a flat 1.05 m stream would read as flying UNDER the boss; plan-gate r1 opus P1,
  // evidence frame l3_z452_boss). Non-heli levels pass nothing: the Fx default is the shipped
  // tank literals, so L1/L2 stay bit-identical.
  const bossFx: BossFx | undefined =
    level.boss.kind === 'heli'
      ? { fireY: HELI_HOVER_Y + HELI_PARTS.podY, hitY: HELI_HOVER_Y, tracerY: HELI_HOVER_Y,
          // impl-gate r2: ramp over the squad→hit-wall distance so hit tracers peak AT the hull.
          tracerRiseSpan: level.boss.standoff - 2 }
      : undefined;
  const fx = new Fx(scene, quality.caps.bulletPool, quality.caps.maxSoldierInstances, quality.caps.fxParticles, level.weapons, bossFx);
  fx.setMuzzleProvider(slot => squad.muzzlePosition(slot));
  fx.setBossFireOrigin(snapshot => level.boss.kind === 'heli'
    ? heliMuzzle(level.boss, snapshot) : tankMuzzle(level.boss, snapshot));
  // Plan 14 P14.3: barrel shards, next to Fx with the same tier-caps pattern — two thin-instance
  // pools (+2 draws at peak detonation, 0 while idle) fed by the same drained explosion events.
  const debris = new Debris(scene, materials, level, quality.caps.debrisEvents);
  // Plan 20 P20.4: the ambient system (lane C) — the High-tier gate lives INSIDE the class
  // (tier !== 'high' no-ops everything: 0 draws, 0 meshes, 0 cost). The `?pilot` runtime switch
  // (on/off, absent = the recorded OFF disposition) is read by ambient.ts itself. P20-5: the real
  // backend is threaded in so the pilot's WebGL2-only guard sees it (WebGPU would throw — no
  // compute platform-class imported).
  const ambient = new Ambient(scene, envTheme, tier, quality.caps.bridgeSegments, backend);
  // Read once at boot (plan 08 G2): reduced motion zeroes the camera impulse.
  const impulseScale = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 1;
  const hud = createHud();
  const audioPilot = new URLSearchParams(location.search).get('audioPilot') === '1';
  const levelTheme = LEVEL_THEMES[levelIndex] ?? THEME_L1;
  // audit #15: the pre-plan-24 engine snapshot (src/audio/legacy/, hash-pinned by legacy.test.ts)
  // only serves the `?audioPilot=1` hatch — lazy-imported so it never ships in the main chunk.
  // The non-pilot path stays synchronous; setGaitBaseHz keeps reading the NEW theme in both
  // modes (pre-existing plan-24 behaviour).
  let audio: AudioDirector;
  if (audioPilot) {
    const [legacyAudio, legacyMusic] = await Promise.all([import('./audio/legacy/audio'), import('./audio/legacy/music')]);
    const legacyLevelThemes = [legacyMusic.THEME_L1, legacyMusic.THEME_L2, legacyMusic.THEME_L3];
    audio = Object.assign(legacyAudio.createAudio(legacyLevelThemes[levelIndex] ?? legacyMusic.THEME_L1), { playUi: async () => {} });
  } else {
    audio = createAudio(levelTheme, { bossKind: level.boss.kind ?? 'brute', enemyLookahead });
  }
  setGaitBaseHz(levelTheme.bpm / 30); // plan 24: the gait reads at the theme's quick-march tempo
  hud.bindMute(audio);
  // Plan 16 P16.2: automation (smoke/inspect — the same navigator.webdriver key audio.ts
  // shouldStaySilent() uses) auto-starts so unattended sim advance keeps working; real users
  // get the start screen.
  let started = !lightSave && navigator.webdriver === true && (!expeditionSave || expeditionSave.run.status === 'ready');
  const startOverlay = document.getElementById('start')!;
  const startBtn = document.getElementById('start-btn')!;
  let lightActionPending = false;
  const lightUi = lightSave ? createLightUi({
    deploy: async t => {
      if (lightActionPending) return;
      lightActionPending = true; lightUi!.busy(true);
      try {
        await audio.playUi('confirm');
        travelLight(deployLight(lightSave!, t));
      } finally { lightActionPending = false; lightUi!.busy(false); }
    },
    allocate: async n => {
      if (lightActionPending) return;
      lightActionPending = true; lightUi!.busy(true);
      const previous = lightSave!;
      const next = allocateLight(previous, n);
      saveLight(next); lightUi!.status(next);
      try {
        await audio.playUi(n ? 'dispatch' : 'confirm');
        if (next.run.status === 'complete') await audio.playUi('operationComplete');
        else if (Math.floor(next.home / 50) > Math.floor(previous.home / 50)) await audio.playUi('tierComplete');
      } finally {
        lightUi!.show(next, lightStore.persistent);
        lightActionPending = false; lightUi!.busy(false);
      }
    },
    restart: () => travelLight(newLight(lightSave!)),
  }) : null;
  if (lightSave) { lightUi!.status(lightSave); startBtn.textContent = 'DEPLOY LAST LIGHT'; const tag = startOverlay.querySelector<HTMLElement>('.tag'); if (tag) tag.textContent = 'Support the invasion. A/D, arrows or drag to steer. Firing is automatic.'; }
  else mountLightEntry();
  const expeditionUi = isExpedition ? createExpeditionUi({
    onRoute: (route) => {
      if (!expeditionSave) return;
      const next = chooseExpeditionRoute(expeditionSave, route);
      if (next !== expeditionSave) travelExpedition(next);
    },
    onExtract: () => {
      if (!expeditionSave) return;
      saveExpedition(extractExpedition(expeditionSave));
      expeditionUi!.show(expeditionSave, expeditionStore.persistent);
      expeditionUi!.setStatus(expeditionSave);
    },
    onNewRun: launchExpedition,
    onCampaign: () => location.replace(campaignHref(location.href)),
  }) : null;
  if (expeditionSave) {
    expeditionUi!.setStatus(expeditionSave);
    startBtn.textContent = 'DEPLOY';
    const tag = startOverlay.querySelector<HTMLElement>('.tag');
    if (tag) tag.textContent = `Seed: ${expeditionSave.run.seed}. Keep your squad and weapons between bridges.${expeditionStore.persistent ? '' : ' Storage unavailable; keep this tab open.'}`;
  } else if (!lightSave) {
    mountExpeditionEntry(savedExpedition, launchExpedition, () => {
      if (savedExpedition) travelExpedition(savedExpedition);
    });
  }
  bindAudioControls(audio);
  let deploying = false;
  startBtn.addEventListener('click', async () => {
    if (deploying) return;
    if (lightSave && lightSave.run.status !== 'ready') return;
    if (expeditionSave && expeditionSave.run.status !== 'ready') return;
    if (lightSave) {
      deploying = true; (startBtn as HTMLButtonElement).disabled = true;
      try { await audio.playUi('deploy'); }
      finally { deploying = false; (startBtn as HTMLButtonElement).disabled = false; }
    }
    started = true;
    startOverlay.style.display = 'none';
    // Impl-gate r1 opus P2-6: a focused slider must not eat arrow keys mid-run.
    (document.activeElement as HTMLElement | null)?.blur();
    audio.unlock(); // the first click enables sound — explicit, not incidental
    // Plan 17 D2: the intro card is the LAST statement — a DOM failure in it can never break the
    // start gate or the audio unlock. Decorative DOM only: the auto-started webdriver path never
    // reaches this handler.
    hud.showLevelIntro(expeditionSave?.run.depth ?? levelIndex + 1, level.name);
  });
  const input = new InputController(canvas);
  const debug = readDebugHooks(new URLSearchParams(location.search));
  // P5.1 measured budget gate: the draw-call counter lives on SceneInstrumentation (there is no
  // `Instrumentation` façade class in @babylonjs/core 9.28). Debug-only — zero cost in a normal run.
  const instrumentation: SceneInstrumentation | null = debug.enabled ? new SceneInstrumentation(scene) : null;

  // --- plan 05 P5.6: character shadows (High tier only) ---------------------------------------
  // The rig exists only when caps.realShadows; on Low/Medium `shadows` is null, no 1024² render
  // target is allocated and the squad blob keeps doing the grounding.
  // Scene handles for the shadow/blob wiring: `scene.meshes` is AbstractMesh[] in 9.28, so the
  // Mesh-only members (getTotalIndices, thinInstanceCount) need the same cast the probe uses.
  const characterMesh = (name: string): Mesh | null =>
    (scene.meshes.find((m) => m.name === name) as Mesh | undefined) ?? null;
  const squadBlob = characterMesh('squad-shadow');
  if (shadows) {
    // Casters: the squad family + the boss body. The enemy mesh is deliberately NOT a caster (dossier
    // §5: enemies keep the cheap blob read on every tier). With the skeletal squad (plan 07) the
    // family is the disabled source clone-parent + one skinned clone per soldier slot — each must
    // be registered or whole soldiers drop their shadows. All handles come from the scene by probe
    // name — a renamed caster must fail at boot, not silently drop shadows. The tank (plan 15)
    // adds its turret and barrel: three rigid casters where the brute had one body.
    const casterNames = useSkeletalSquad
      ? [...(squad as SkeletalSquad).casterNames(), 'boss-torso']
      : ['soldier', 'boss-torso'];
    if (level.boss.kind === 'tank') casterNames.push('boss-turret', 'boss-barrel');
    // Plan 19 D3: the heli's spinning main disc is its second caster family member (the hull
    // itself is the shared 'boss-torso' base caster; the tail rotor stays uncasted).
    if (level.boss.kind === 'heli') casterNames.push('boss-rotor');
    for (const name of casterNames) {
      const mesh = characterMesh(name);
      if (!mesh) throw new Error(`[boot] shadow caster "${name}" is missing — the actor mesh names are the gate's handles`);
      // The caster triangle term (P5.1/P5.6) is only meaningful for an indexed mesh (r4).
      if (!(mesh.getTotalIndices() > 0)) {
        throw new Error(`[boot] shadow caster "${name}" is not indexed (getTotalIndices() === 0)`);
      }
      shadows.addCaster(mesh);
    }
    // Receivers: the deck (asphalt merge) + the rails/skirt (concrete merge). Casters with nothing to
    // fall on render no shadow at all, so a missing receiver is a boot error, not a tuning task.
    //
    // WHY REGISTERING ONCE AT BOOT IS ENOUGH (impl gate F6, verified against world.ts `Bridge`):
    // the ring is recycled by MOVING ROOTS. `buildSegment(index)` — which is the only place a
    // segment's meshes are ever created — runs exactly once per segment in the constructor, and
    // `update(squadZ)` calls nothing but `place(root, z)`, i.e. `root.position.z = z`. The children
    // (the two merged meshes registered here, parented to the root) are persistent objects that are
    // never disposed, re-parented or rebuilt, so the AbstractMesh identities captured at boot stay
    // the identities the engine renders for the whole run; the only thing that changes is which
    // stretch of river they sit over. A per-frame re-registration would be re-doing work Babylon
    // already does through the parent transform — and `freezeWorldMatrix()` is deliberately NOT used
    // (world.ts), because it would snapshot the boot-time matrix and strand the children at
    // z ∈ [0, 40] (plan 03 root cause). That is also why `debugChildZMax()` has to
    // `computeWorldMatrix(true)` before reading the bounds.
    let receiverCount = 0;
    for (const root of bridge.debugRoots()) {
      for (const child of root.getChildMeshes()) {
        if (child.name.startsWith('deck-') || child.name.startsWith('skirt-')) {
          shadows.addReceiver(child);
          receiverCount++;
        }
      }
    }
    if (receiverCount === 0) {
      throw new Error('[boot] no shadow receivers found — the bridge deck/rail mesh names changed');
    }
    // Measurement lever (debug builds only, see DebugHooks.noShadow): ?debug=1&noshadow=1 takes the
    // whole shadow pass out of the frame so the gate can diff the counters against the shadowed run.
    if (debug.enabled && debug.noShadow) shadows.setEnabled(false);
  }
  // Squad blob shadow: off on High (Design 5 — no double shadow under the real cascade). The ENEMY
  // blob (`enemy-shadow`, actors.ts Enemies) is NOT touched here and stays on every tier: the enemy
  // mesh is deliberately not a shadow caster, so that disc is the block's only grounding
  // (plan 05 §P5.6 "blob stays for enemies on all tiers"; impl gate F2 closed the gap where the
  // mesh did not exist at all before P5.2 replaced the mannequin crowd).
  // audit #11: the boot-time disable alone no longer holds — Squad.update() re-asserts the disc
  // every frame (shown > 0), so High-tier ?squad=procedural runs must latch it off through the
  // actor (disableBlob also setEnabled(false)s the mesh; the probe's blobSquad stays false).
  if (squadBlob && quality.caps.realShadows) squad.disableBlob();

  let sim: SimState = lightSave ? createExpeditionSim(level, lightSave.run.loadout) : expeditionSave ? createExpeditionSim(level, expeditionSave.run.loadout) : createSim(level);
  hud.setWeapon(level.weapons[sim.weaponTier].label, sim.ratePips);
  const restart = (): void => {
    sim = createSim(level);
    hud.hideEnd();
    hud.setWeapon(level.weapons[0].label);
    hud.setBoss(false);
    fx.reset();
    debris.reset();
    ambient.reset(); // the pilot's live embers never rewind with sim.t; the quad pool self-corrects
    audio.reset();
    gates.reset();
    hazards.reset(); // one-shot latch is sim state; the props have to come back with it
    bridge.reset(); // the segment ring only recycles forward; sim z rewinds to 0
  };
  // Plan 21: the end latch's target level (1-based) — set where showEnd fires, read by the
  // overlay buttons below. ONE source of truth for the action (in-place restart vs jump): the
  // buttons never read storage back on click, so a demoted store can never disagree with the
  // label the player is reading (plan-gate r1 P1-1).
  let endTarget: number | null = null;
  /**
   * Plan 21: navigate to the target level. The store was already written at the end latch, so a
   * persistent store just strips `?level=` and reloads (clean address bar); a store demoted to
   * memory SETS `?level=target` — the override doubles as the transport, because the memory
   * value dies with the page (plan-gate r1 P0-1: plain reload there would strand the player on
   * L1 forever). replace() adds no history entry, so Back can never resurrect a ?level= URL.
   */
  const jump = (target: number): void => {
    const href = nextLevelHref(campaignHref(location.href), target, levelStore.persistent);
    if (href === location.href) location.reload();
    else location.replace(href);
  };
  hud.onRestart(() =>
    // In-place exactly when the target IS the current level (an L1 death; the 1-level edge of a
    // final win) — today's instant retry. Every other end state is a jump: a death beyond L1
    // restarts the run at L1, the final-level win loops back to L1 (plan 21). The rule is
    // restartsInPlace (levelStore.ts) so the label/action parity is test-pinned (impl-gate
    // opus P2-2).
    restartsInPlace(endTarget ?? levelIndex + 1, levelIndex) ? restart() : jump(endTarget ?? 1),
  );
  hud.onNextLevel(() => {
    // Same page, next level (no menu system): the store already holds it (written at the win
    // latch), so the jump is pure navigation.
    jump(endTarget ?? levelIndex + 2);
  });

  window.addEventListener('resize', () => {
    engine.resize();
    quality.resize();
  });

  if (debug.enabled) {
    window.__jreb1dResume = (nextZ?: number) => {
      debug.paused = false;
      debug.pauseAtZ = nextZ !== undefined && Number.isFinite(nextZ) ? nextZ : null;
    };
  }

  let simTime = 0;
  let wallLast = performance.now();
  let ended = false;
  let resultsShown = false;
  let defeatAge = 0;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const skipDefeat = document.createElement('button');
  skipDefeat.id = 'skip-defeat';
  skipDefeat.type = 'button';
  skipDefeat.textContent = 'Skip animation';
  skipDefeat.style.display = 'none';
  document.body.append(skipDefeat);
  skipDefeat.onclick = () => { defeatAge = DEFEAT_SECONDS; };
  window.addEventListener('keydown', event => {
    if (event.key === 'Escape' && sim.phase === 'won') defeatAge = DEFEAT_SECONDS;
  });
  let lastN = -1;

  engine.runRenderLoop(() => {
    const now = performance.now();
    const frameElapsed = Math.max(0, (now - wallLast) / 1000);
    const frameDt = Math.min(0.1, frameElapsed);
    wallLast = now;

    const targetX = started ? input.poll(frameDt) : 0; // plan 16: a held key must not integrate a steering offset behind the start gate
    // The gate is checked on two clocks on purpose (plan 04 fix round): once per FRAME here, so a
    // `__jreb1dResume(z)` pointing behind the squad still parks it immediately, and once per SUBSTEP
    // below, so the overshoot when the squad CROSSES the trigger is bounded by one SIM_DT (~0.083 m)
    // instead of a whole frame's worth — which matters because both opening shots stop only 6 m short
    // of their level's first gate (L1 z12_early → z=18, L2 l2_z20_early → z=26) and a crossed pair
    // is hidden again by Gates.update.
    const atPauseGate = (): boolean => debug.pauseAtZ !== null && sim.squadZ >= debug.pauseAtZ;
    const simPaused = debug.paused || atPauseGate();
    if (simPaused && !debug.paused) debug.paused = true;
    if (!simPaused && started) {
      const steps = Math.ceil(MAX_STEPS_PER_FRAME * debug.simSpeed); // integer: the fall-behind check below compares with ==
      let taken = 0;
      simTime += frameDt * debug.simSpeed;
      while (taken < steps && sim.t + SIM_DT <= simTime) {
        stepSim(level, sim, SIM_DT, { targetX });
        taken++;
        if (debug.godmode && sim.n <= 0) sim.n = 1;
        // Plan 15: the no-steer probe run now dies on the enriched tracks (L1 loses its last
        // soldier at the z-398 barrel chain with no gate after 375 to regrow it), and godmode's
        // 1-soldier clamp left the boss shots a single aliased anim slot. Floor the crowd at
        // boss entry to the High soldier cap — the budget shots keep their worst-case crowd
        // (48 shown on main measured n 45–65 anyway), run-phase shots keep the natural crowd.
        if (debug.godmode && sim.phase === 'boss' && sim.n < GODMODE_BOSS_N_FLOOR) sim.n = GODMODE_BOSS_N_FLOOR;
        if (atPauseGate()) {
          debug.paused = true;
          break; // substep-exact pause: no further advance this frame
        }
      }
      if (taken >= steps) simTime = sim.t; // fell behind: drop time, don't spiral
      if (debug.godmode && (sim.phase === 'lost' || sim.phase === 'won')) {
        sim.phase = 'run';
        sim.n = Math.max(sim.n, 1);
      }
      // audit #14: read only by buildProbe (?debug=1) — the filter array must not allocate per frame in shipped runs.
      if (debug.enabled) debug.bulletCountMax = Math.max(debug.bulletCountMax, sim.bullets.filter((b) => b.alive).length);
    }

    const events = drainEvents(sim);
    fx.handleEvents(events, sim);
    debris.handleEvents(events); // the explosion events drain feeds the shard pools too
    gates.handleEvents(events);
    audio.update(events, sim, simPaused || (!started && sim.phase !== 'won' && sim.phase !== 'lost'));
    bridge.update(sim.squadZ, sim.t);
    squad.update(sim, quality.caps.maxSoldierInstances);
    // Blob sizing on the tiers that still use it (P5.6): the squad blob is sized to the crowd here.
    if (squadBlob && !quality.caps.realShadows) sizeSquadBlob(squadBlob, Math.min(sim.n, quality.caps.maxSoldierInstances));
    enemies.update(sim);
    gates.update(sim);
    crates.forEach((prop, i) => prop.update(sim.crates[i]?.broken ?? false));
    obstacles.update(sim);
    hazards.update(sim, simPaused ? 0 : frameDt); // wall-clock flash must freeze with the sim
    footprint.update(sim);
    pickups.update(sim);
    defeatAge = nextDefeatAge(defeatAge, sim.phase === 'won', ended ? frameElapsed : 0, simPaused, reducedMotion.matches);
    boss.update(sim, frameDt, sim.phase === 'won' ? defeatAge : undefined);
    bossContact.update(sim, sim.phase === 'won' ? defeatAge : undefined);
    const effectSnapshot = sim.phase === 'won' ? { ...sim, t: sim.t + defeatAge } : sim;
    fx.update(effectSnapshot);
    debris.update(effectSnapshot);
    // Plan 20 P20.4: ambient after the Bridge's update(squadZ) — the brazier slot→world mapping
    // reads the converged recycle state (ambient.ts header). The freeze call dedupes internally;
    // `|| !started` keeps the field parked behind the start gate like the rest of the render layer.
    ambient.update(sim);
    ambient.setFrozen(simPaused || !started);

    // Chase camera: locked rail behind the squad (plan 03 framing contract: below the
    // 4.95–5.45 beam band, squad in the lower half, unclipped — asserted by the probe).
    const camX = toWorldX(sim.squadX) * 0.5;
    // Plan 20 P20.4: the gate-pass whoosh list is stamped by fx.handleEvents from the drained
    // `gatePass` sim events (pruned against sim.t); the boss-fire recoil reads sim.bossFireAt
    // inside cameraImpulse directly. Still pure f(sim-recorded times + snapshot), ≤ 0.12 m.
    const shake = cameraImpulse(effectSnapshot, impulseScale, fx.debugGatePasses());
    const framing = chaseFraming(canvas.clientWidth, canvas.clientHeight, level.boss.z - sim.squadZ, cameraFraming);
    camera.fov = framing.fov;
    camera.position.set(camX + shake.x, framing.height + shake.y, sim.squadZ - framing.distance);
    camera.setTarget(camTarget.set(toWorldX(sim.squadX) * 0.8 + shake.x, 1.1 + shake.y, sim.squadZ + 11)); // audit #14: scratch, no per-frame Vector3

    // Shadow cascade follows the squad (fixed offsets, texel-snapped — see CharacterShadowRig).
    // The ONLY time-varying input to the shadow path, and it is pure sim state: under the inspect
    // pause the anchor stops moving, the light is not even rewritten, and repeated paused frames
    // render the same depth image.
    shadows?.follow(toWorldX(sim.squadX), sim.squadZ);

    // Plan 09 HP bars, placed with THIS frame's camera: getTransformationMatrix() would return last
    // frame's cached product (the camera moved above). Before buildProbe, which reads the DOM back.
    camera.getViewMatrix(true).multiplyToRef(camera.getProjectionMatrix(true), viewProj);
    hud.setHpBars(projectHpBars(hpBars(sim, level), viewProj, canvas.clientWidth, canvas.clientHeight));

    // HUD.
    if (sim.n !== lastN) {
      hud.setSquad(sim.n);
      lastN = sim.n;
    }
    // Expeditions show the post-gate squad early enough to plan the firing lane. `threat` is the HUD's
    // feed; the probe's copy is recounted from raw sim.enemies (see recountEnemyM) so the two are
    // the same frame's truth without being the same variable.
    const threat = enemyThreat(sim, enemyLookahead);
    hud.setEnemy(
      threat ? threat.count : null,
      threat ? projectPoint(toWorldX(threat.x), ENEMY_LABEL_Y, threat.z, viewProj, canvas.clientWidth, canvas.clientHeight) : undefined,
    );
    hud.setBoss(sim.phase === 'boss', sim.bossHp / sim.bossMaxHp);
    for (const event of events) {
      if (event.type === 'weaponUp') {
        hud.setWeapon(level.weapons[event.tier].label, sim.ratePips);
      } else if (event.type === 'weaponMod') {
        hud.setWeapon(level.weapons[sim.weaponTier].label, event.pips, event.gained);
      }
    }
    if (!ended && (sim.phase === 'won' || sim.phase === 'lost')) {
      ended = true;
      if (lightSave) {
        started = false;
        saveLight(settleLight(lightSave, { won: sim.phase === 'won', n: sim.n, weaponTier: sim.weaponTier, ratePips: sim.ratePips }));
        lightUi!.status(lightSave);
      } else if (expeditionSave) {
        started = false;
        saveExpedition(resolveExpedition(expeditionSave, {
          won: sim.phase === 'won', n: sim.n, weaponTier: sim.weaponTier, ratePips: sim.ratePips,
        }));
        expeditionUi!.setStatus(expeditionSave);
      } else {
        // Campaign progress is settled before navigation, including reload on the end screen.
        endTarget = nextStoredLevel(sim.phase === 'won', levelIndex, SHIPPED_LEVELS.length);
        levelStore.write(endTarget);
      }
    }
    const waitingForDefeat = sim.phase === 'won' && defeatAge < DEFEAT_SECONDS;
    skipDefeat.style.display = waitingForDefeat ? 'block' : 'none';
    if (ended && !resultsShown && !waitingForDefeat) {
      resultsShown = true;
      if (lightSave) lightUi!.show(lightSave, lightStore.persistent);
      else if (expeditionSave) expeditionUi!.show(expeditionSave, expeditionStore.persistent);
      else hud.showEnd(sim.phase === 'won', sim.n, sim.t, {
        levelName: level.name, nextLevel: hasNextLevel, atFirstLevel: levelIndex === 0,
      });
    }
    if (sim.phase === 'run') { ended = false; resultsShown = false; }

    // Debug pause holds the sim; stepping the render scale then would make paused frames differ.
    if (!(debug.enabled && simPaused)) quality.frame();
    if (debug.enabled) {
      hud.debug(
        `${backendLabel}  tier=${tier}  scale=${quality.renderScale.toFixed(2)}\n` +
          `fps=${engine.getFps().toFixed(0)}  meshes=${scene.getActiveMeshes().length}  z=${sim.squadZ.toFixed(0)}m`,
      );
    }

    if (debug.enabled) {
      try {
        debug.probe = { ...buildProbe(debug, engine.getFps(), scene, sim, level, bridge, squad, enemies, hazards, obstacles, hud, threat, camera, quality.tier, backend, instrumentation, shadows, squadBlob, pipeline, enemyLookahead), ambient: ambient.debugPilot(), audioState: audio.state, defeat: { age: defeatAge, duration: DEFEAT_SECONDS, resultsShown },
          ...(expeditionSave ? { expedition: expeditionSave, expeditionSaved: expeditionStore.persistent,
            expeditionEncounters: expeditionGeneration?.encounters, expeditionPressure: expeditionGeneration?.pressure,
            expeditionFallback: expeditionGeneration?.fallbackReason } : {}),
        };
        window.__jreb1dDebug = debug.probe as Record<string, unknown>;
      } catch (error) {
        // The probe must never take the game down.
        window.__jreb1dDebug = { probeError: String(error) };
      }
    }

    scene.render();
  });

  // --- task 14: the reveal gate ---------------------------------------------------------------
  // The render loop is running from here on; Babylon's readiness is only recorded WHILE it renders,
  // so the gate is polled on presented frames (engine.onEndFrameObservable), never on a wall clock
  // alone. `scene.isReady(true)` is the exact predicate the engine uses to skip a mesh, and
  // `scene.environmentTexture` (the IBL) is the one asset `createWorldScene` starts and does not
  // await — a late arrival pops the lighting under the start card.
  //
  // Hiding `#loading` when the awaited texture downloads landed (the old behaviour) therefore
  // revealed a frame containing NOTHING but `scene.clearColor` — the fog grey rgb(191,196,209) on
  // L1, i.e. the owner's "white screen after loading bar", with the card's blur on top of it. The
  // wait is bounded (REVEAL_BUDGET_MS): a device that never compiles its shaders gets the game
  // anyway instead of a permanent loading screen, and says so in `reason: 'budget'`.
  const boot: BootReadout = {
    framesRendered: 0,
    revealedAt: null,
    reason: null,
    waitingFor: 'frames<2',
    waitedMs: 0,
    sceneReadyAtReveal: false,
    environmentReadyAtReveal: false,
    budgetMs: REVEAL_BUDGET_MS,
  };
  (window as { __jreb1dBoot?: BootReadout }).__jreb1dBoot = boot;
  const revealStartedAt = performance.now();
  const frameObserver = engine.onEndFrameObservable.add(() => {
    boot.framesRendered++;
  });
  let decision: RevealDecision;
  try {
    decision = await waitForReveal(
      (): RevealSnapshot => ({
        framesRendered: boot.framesRendered,
        sceneReady: scene.isReady(true),
        environmentReady: scene.environmentTexture?.isReady() ?? true,
        elapsedMs: performance.now() - revealStartedAt,
        documentHidden: document.hidden,
      }),
      (notify) => {
        const observer = engine.onEndFrameObservable.add(notify);
        return () => engine.onEndFrameObservable.remove(observer);
      },
      { budgetMs: REVEAL_BUDGET_MS },
    );
  } catch (error) {
    // The gate must never become the reason the game is not shown: a readout that throws falls back
    // to the old unconditional reveal (with the reason recorded), rather than keeping the player
    // behind a loading bar that can no longer come down.
    console.warn('[boot] reveal gate threw — revealing unconditionally:', error);
    decision = { reveal: true, reason: 'budget', waitingFor: `gate-error: ${String(error)}` };
  } finally {
    engine.onEndFrameObservable.remove(frameObserver);
  }
  boot.reason = decision.reason;
  boot.waitingFor = decision.waitingFor;
  boot.waitedMs = performance.now() - revealStartedAt;
  boot.sceneReadyAtReveal = decision.reason === 'painted' || scene.isReady(true);
  boot.environmentReadyAtReveal = decision.reason === 'painted' || (scene.environmentTexture?.isReady() ?? true);
  if (decision.reason === 'budget') {
    console.warn(`[boot] reveal gate budget exhausted — revealing with the scene still not ready (${decision.waitingFor})`);
  }

  loading.style.display = 'none';
  boot.revealedAt = performance.now();
  if (lightSave && lightSave.run.status !== 'ready') {
    lightUi!.show(lightSave, lightStore.persistent);
  } else if (expeditionSave && expeditionSave.run.status !== 'ready') {
    expeditionUi!.show(expeditionSave, expeditionStore.persistent);
  } else if (!started) {
    startOverlay.style.display = 'flex';
    startBtn.focus(); // keyboard players: Enter/Space starts
  }
}

/**
 * Independent recount of the M the HUD has to be showing (plan 04 fix round). `threat.count` is the
 * very number that was just fed to `hud.setEnemy`, so asserting the DOM readback against it would
 * compare a value to itself and could never fail. This walks the raw `sim.enemies` block at the
 * threat's z and re-checks the invariants that made it the threat (alive, not engaged, ahead of the
 * squad, inside ENEMY_LOOKAHEAD) — a dropped write, a wrong-element write or a hidden readout now
 * disagrees with it. 0 means "nothing should be displayed", matching the probe's numeric shape.
 */
function recountEnemyM(sim: SimState, threat: { z: number; count: number } | null, lookahead: number): number {
  if (!threat) return 0;
  const block = sim.enemies.find(
    (e) =>
      e.z === threat.z &&
      !e.engaged &&
      e.count > 0 &&
      e.z > sim.squadZ &&
      e.z - sim.squadZ <= lookahead,
  );
  return block ? block.count : 0;
}

/** The deterministic visibility probe (plan 03 Phase 1). Runs only with ?debug=1. */
function buildProbe(
  debug: DebugHooks,
  fps: number,
  scene: Scene,
  sim: SimState,
  level: LevelSpec,
  bridge: Bridge,
  squad: SquadSurface,
  enemies: Enemies,
  hazards: Hazards,
  obstacles: Obstacles,
  hud: Hud,
  threat: { z: number; count: number } | null,
  camera: FreeCamera,
  tier: string,
  backend: 'webgpu' | 'webgl2',
  instrumentation: SceneInstrumentation | null,
  shadows: CharacterShadows | null,
  squadBlob: Mesh | null,
  pipeline: DefaultRenderingPipeline | null,
  enemyLookahead: number,
): Record<string, unknown> {
  const active = new Set(scene.getActiveMeshes().data.filter((m) => m !== null).map((m) => m.name));
  const instanced = [squad.debugThinInstances(), enemies.debugThinInstances()];
  // Plan 07: with the skeletal squad the drawn soldiers are the `soldier-anim-*` family (the
  // 'soldier' source is the disabled clone parent), so "is the squad active" is a family question.
  // The count semantics are unchanged: instances drawn this frame ≤ the tier cap.
  const soldierActive = active.has('soldier') || [...active].some((n) => n.startsWith('soldier-anim-'));
  // P5.6 shadow payload. `shadows.enabled` is the only claim about whether the pass runs;
  // casters/receivers are MEMBERSHIP facts read off the live shadow map render list / the rig's
  // receiver list, never a re-derivation from intent. Nothing here claims the shadows were visible —
  // that is the screenshots' job (plan 05 §P5.6 verification honesty).
  const shadowsOn = shadows?.enabled ?? false;
  const shadowState = shadows
    ? shadows.debugState()
    : { enabled: false, mapSize: 0, casters: [] as string[], receivers: [] as string[] };
  // The nearest enemy contact instance exists on every tier. Its batch also holds prop contacts;
  // debugBlob reads enemy membership and dimensions from the written instance buffer.
  const enemyBlob = enemies.debugBlob();
  const shadowPayload = {
    ...shadowState,
    blobSquad: !!squadBlob && squadBlob.isEnabled() && squadBlob.isVisible,
    blobEnemies: enemyBlob.enabled && enemyBlob.visible,
  };
  const shadowCasterTris = shadowsOn ? shadowCasterTriangles(shadows!.casterMeshes()) : 0;
  const meshes = [
    ...instanced.map((info) => ({
      name: info.name,
      thinInstanceCount: info.count,
      active: info.name === 'soldier' ? soldierActive : active.has(info.name),
      instZMin: info.zMin,
      instZMax: info.zMax,
    })),
    // 'boss-rotor' (plan 19 D3, the heli's main disc) joins the watch list; the meshes-list guard
    // already reports a missing mesh as an inactive row, so brute/tank levels are unaffected.
    ...['boss-torso', 'boss-turret', 'boss-barrel', 'boss-rotor', 'crate', 'water'].map((name) => {
      const mesh = scene.meshes.find((m) => m.name === name) as Mesh | undefined;
      if (!mesh) return { name, thinInstanceCount: 0, active: false, instZMin: null, instZMax: null };
      const bb = mesh.getBoundingInfo().boundingBox;
      return { name, thinInstanceCount: mesh.thinInstanceCount, active: active.has(name), instZMin: bb.minimumWorld.z, instZMax: bb.maximumWorld.z };
    }),
  ];
  return {
    fps: Number(fps.toFixed(1)),
    // P5.1: `backend` is now the machine-checkable engine backend string (inspect asserts it equals
    // the pinned `?gfx=`); the human-readable label this field used to hold ships on as
    // `backendLabel`, so the recorded manifests keep every previous string.
    backend,
    backendLabel: (window as { __jreb1dBackend?: string }).__jreb1dBackend,
    tier,
    // Measured budget counters (P5.1). Both are one frame behind by construction: the probe is
    // rebuilt before scene.render(), and both counters fetch a new frame at the start of rendering.
    // Every material/effect compiled and render targets ready; until then meshes are skipped and the counters under-read.
    sceneReady: scene.isReady(true),
    drawCalls: instrumentation?.drawCallsCounter.current ?? 0,
    activeTris: scene.getActiveIndices() / 3,
    // Plan 20 P20.4: the active-mesh count is a gate datum now — tools/inspect.mjs publishes it
    // on the BUDGET line and the expectation-relative active-mesh guard reads it against the
    // P20-0 baseline (High 46/66/98/68/98/68; ambient adds exactly +1 on High).
    activeMeshes: scene.getActiveMeshes().length,
    // P5.6: when the cascade runs, the budget sum gains the explicit shadow-caster term
    // (Σ caster getTotalIndices()/3 × max(1, thinInstanceCount) — the max(1,·) keeps the non-thin
    // boss caster nonzero, and main.ts asserts every caster is indexed at boot). The main pass
    // already counts thin instances, so this term over-counts whatever the depth pass contributes:
    // conservative by design. The actual overlap is measured gate-side — tools/inspect.mjs re-runs
    // the boss shot with ?noshadow=1 and records the activeTris delta as `shadowTriOverlap`; the
    // probe cannot know the other page's number, so it stays null here (never a claim).
    triMethod: shadowsOn ? 'activeIndices+shadowCasters' : 'activeIndices',
    shadowCasterTris,
    shadowTriOverlap: null,
    shadows: shadowPayload,
    // Plan 08 G1: the EFFECTIVE sample count (read back after assignment; WebGL2 clamps) and bloom state.
    post: { bloom: pipeline?.bloomEnabled ?? false, msaaSamples: pipeline?.samples ?? 1 },
    squadZ: sim.squadZ,
    squadX: sim.squadX,
    bossWarning: sim.slam && sim.slam.active !== 'silent' ? { zone: [...sim.slam.zone] } : null,
    // P5.3: recorded sim time — the anim-change assertion uses THIS (never z-derived
    // arithmetic), so the |sin| bob period (≈0.263 s) cannot alias a shot pair.
    simT: sim.t,
    // P5.3: per-instance procedural-motion sample (two unit-test-locked slots).
    anim: squad.debugAnim(),
    // Plan 07: skeletal-path facts (null on the procedural tiers) — truthful membership for the
    // gate, same rule as the shadow payload.
    skeletalSquad: squad instanceof SkeletalSquad ? squad.debugSkeletal() : null,
    n: sim.n,
    phase: sim.phase,
    weaponTier: sim.weaponTier,
    ratePips: sim.ratePips,
    streams: streamsFor(sim.n),
    paused: debug.paused,
    bulletCount: debug.paused ? debug.bulletCountMax : sim.bullets.filter((b) => b.alive).length,
    bridgeChildZMax: bridge.debugChildZMax(),
    meshes,
    // P1.4: hazard props + latched/hit state (the inspect hazard shots land with L2 in P1.7).
    hazards: hazards.debugHazards(sim),
    // Plan 09: obstacle truth, and the HP bars the DOM shows vs the pure rule's selection (same
    // frame, both rounded to 3 dp, selection order) — inspect asserts they are deep-equal.
    obstacles: obstacles.debugObstacles(sim),
    hpBars: hud.debugHpBars(),
    hpBarsSim: hpBars(sim, level).map((b) => ({ key: b.key, frac: Math.round(b.frac * 1000) / 1000 })),
    // P1.5: the M the HUD is displaying (read back off the DOM) vs an independent recount of the
    // sim's own headcount, from the same frame. The gate tooling asserts these are equal — instance
    // counts are capped, so they prove nothing alone, and a cached HUD argument would prove nothing
    // either (plan 04 fix round).
    enemyM: hud.debugEnemyM(),
    enemySimCount: recountEnemyM(sim, threat, enemyLookahead),
    enemyZ: threat ? threat.z : null,
    enemyLookahead,
    framing: squad.debugFraming(camera),
    cameraPos: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
  };
}

/**
 * Blob shadow sizing (plan 05 P5.6, T2 finding). The squad blob is an authored
 * BLOB_AUTHORED_GROUND m ground (actors.ts Squad), scaled on the tiers that still use it to the
 * crowd's centre-to-centre footprint (`crowdExtent`, the same layout `crowdSlot` places — never
 * deeper than MAX_FORMATION_DEPTH) plus a margin for the soldiers' half-bodies and the radial
 * gradient's soft edge (BLOB_MARGIN). The legacy scale was (2, 1, 3) = 5.2 × 7.8 m at any crowd
 * size. On High the real cascade replaces the blob (Design 5).
 *
 * Both constants come from actors.ts (impl gate F4e): the squad blob is sized HERE and the enemy
 * blob in `Enemies.updateBlob` through `blobScaling`, so a local re-declaration would let the two
 * crowds' grounding drift apart while both still read "the blob matches the formation".
 *
 * Pure function of the shown count (the formation math is closed-form), so it stays pause-frozen
 * like everything else in the render layer.
 */
function sizeSquadBlob(blob: Mesh, shown: number): void {
  if (shown <= 0) return;
  // crowdExtent is the same layout crowdSlot places (never deeper than MAX_FORMATION_DEPTH).
  const { width, depth } = crowdExtent(shown);
  blob.scaling.set((width + BLOB_MARGIN) / BLOB_AUTHORED_GROUND, 1, (depth + BLOB_MARGIN) / BLOB_AUTHORED_GROUND);
}

declare global {
  interface Window {
    __jreb1dDebug?: Record<string, unknown>;
    __jreb1dResume?: (nextZ?: number) => void;
  }
}

main().catch((error) => {
  console.error('[boot] failed:', error);
  const loading = document.getElementById('loading');
  if (loading) loading.innerHTML = `<div style="max-width:28em;padding:1em">Failed to start: ${String(error)}</div>`;
});
